// ─── src/EcosystemPerceptionView.tsx ──────────────────────────────────────────
import { useState, useCallback } from "react";
import { Download, Search, X, Star, Filter } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useEcosystemResponses,
  useEcosystemStats,
  useDeleteEcosystemResponse,
  ecosystemKeys,
} from "./hooks/useEcosystemPerception";
import { EcosystemPerceptionTable } from "./components/dashboard/EcosystemPerceptionTable";
import { Donut, Legend, CHART_COLORS } from "./components/dashboard/Charts";
import { HorizontalBarChart } from "./components/dashboard/HorizontalBarChart";
import { Pagination } from "./components/dashboard/Pagination";
import { TableSkeleton } from "./components/ui/Skeleton";
import { toChartEntries, type ChartEntry } from "./types/stats";
import {
  INDICATOR_LABELS,
  type Indicator,
  type EcosystemResponse,
  type EcosystemFilters,
  type NpsBucket,
} from "./types/ecosystemPerception";

const LIMIT = 20;
const INDICATORS: Indicator[] = [
  "trust", "awareness", "reach", "value", "belonging", "communication", "alignment",
];

const recordToEntries = (rec: Record<string, number> = {}): ChartEntry[] => {
  const entries = Object.entries(rec).map(([name, count]) => ({ name, count }));
  const total = entries.reduce((a, e) => a + e.count, 0);
  return toChartEntries(entries, total);
};

// Survey answer keys in section order (Trust → Awareness → Reach → Value →
// Belonging → Communication → Priorities). `n1` (NPS) is intentionally omitted:
// it's already exported as the top-level `NPS` column above.
const ANSWER_KEY_ORDER = [
  "t1", "t2", "t3", "t4",
  "a1", "a2", "a3", "a4",
  "r1", "r2", "r3", "r4",
  "v1", "v2", "v3",
  "b1", "b2", "b3",
  "c1", "c2", "c3",
  "p1", "p2",
];

/** Arrays (checkbox multi-selects) collapse to a single pipe-joined cell. */
const stringifyAnswer = (v: unknown): string => {
  if (Array.isArray(v)) return v.map(String).join(" | ");
  if (v == null) return "";
  return String(v);
};

function exportCsv(rows: EcosystemResponse[]) {
  const esc = (v: unknown) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  // Union of answer keys actually present in the data. Preferred survey order
  // first; any unexpected key (e.g. a newly added question we didn't list)
  // gets appended alphabetically so it still shows up in the export.
  const seen = new Set<string>();
  rows.forEach((r) => Object.keys(r.answers ?? {}).forEach((k) => seen.add(k)));
  const known = ANSWER_KEY_ORDER.filter((k) => seen.has(k));
  const extras = Array.from(seen)
    .filter((k) => !ANSWER_KEY_ORDER.includes(k) && k !== "n1")
    .sort();
  const answerKeys = [...known, ...extras];

  const header = [
    "Name", "Governorate", "City", "Age", "Gender", "Status", "Role", "NPS",
    ...INDICATORS.map((i) => INDICATOR_LABELS[i]),
    ...answerKeys.map((k) => k.toUpperCase()),
  ].join(",");
  const body = rows.map((r) =>
    [
      r.fullNameAr, r.governorate, r.cityArea, r.ageGroup, r.gender,
      r.professionalStatus, r.roleWithStartupSyria, r.nps,
      ...INDICATORS.map((i) => r.scores?.[i] ?? ""),
      ...answerKeys.map((k) => stringifyAnswer(r.answers?.[k])),
    ].map(esc).join(","),
  );
  const blob = new Blob(["﻿" + [header, ...body].join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `ecosystem_perception_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function EcosystemPerceptionView() {
  const qc = useQueryClient();
  const [filters, setFilters]       = useState<EcosystemFilters>({});
  const [visualPage, setVisualPage] = useState(1);

  // Two /stats calls:
  // - `metaStats` (unfiltered) drives dropdown options so filtering can't
  //   empty its own selects.
  // - `stats` (filtered) drives the visible cards and charts so they
  //   always reflect exactly what the table below shows.
  const { data: metaStats } = useEcosystemStats();
  const { data: stats } = useEcosystemStats(filters);

  // Big list used for CSV export — mirrors the current filter so operators
  // export what they're looking at, not the whole dataset by accident.
  const { data: allData } = useEcosystemResponses({ page: 1, limit: 2000, ...filters });
  const allRows = allData?.data ?? [];

  const { data: tableData, isLoading, isFetching, isError } = useEcosystemResponses({
    page: visualPage,
    limit: LIMIT,
    ...filters,
  });

  const { mutate: deleteRow, isPending: isDeleting } = useDeleteEcosystemResponse();

  const pageRows  = tableData?.data ?? [];
  const tableMeta = tableData?.meta ?? { total: 0, page: 1, limit: LIMIT, totalPages: 1 };

  // Dropdown options come from the unfiltered stats so they stay stable.
  const govOptions    = Object.keys(metaStats?.byGovernorate ?? {}).sort();
  const roleOptions   = Object.keys(metaStats?.byRole ?? {}).sort();
  const genderOptions = Object.keys(metaStats?.byGender ?? {}).sort();
  const ageOptions    = Object.keys(metaStats?.byAgeGroup ?? {}).sort();
  const statusOptions = Object.keys(metaStats?.byProfessionalStatus ?? {}).sort();

  const setFilter = <K extends keyof EcosystemFilters>(k: K, v: EcosystemFilters[K]) => {
    setFilters((f) => {
      const next = { ...f };
      if (v == null || v === "") delete next[k];
      else next[k] = v;
      return next;
    });
    setVisualPage(1);
  };
  const clearFilters = () => { setFilters({}); setVisualPage(1); };
  const activeFilterCount = Object.values(filters).filter((v) => v != null && v !== "").length;

  // Indicator averages → chart entries (value is 1–5, % relative to 5)
  const indicatorEntries: ChartEntry[] = INDICATORS.map((i) => {
    const v = stats?.indicatorAverages?.[i] ?? 0;
    return { name: INDICATOR_LABELS[i], value: v, percentage: ((v / 5) * 100).toFixed(0) };
  }).filter((e) => e.value > 0);

  const npsBreakdown: ChartEntry[] = stats
    ? [
        { name: "Promoters", count: stats.nps.promoters },
        { name: "Passives", count: stats.nps.passives },
        { name: "Detractors", count: stats.nps.detractors },
      ]
        .filter((e) => e.count > 0)
        .map((e, _, arr) => {
          const total = arr.reduce((a, x) => a + x.count, 0);
          return { name: e.name, value: e.count, percentage: total ? ((e.count / total) * 100).toFixed(0) : "0" };
        })
    : [];

  const handleRefresh = useCallback(() => {
    qc.invalidateQueries({ queryKey: ecosystemKeys.all });
  }, [qc]);

  return (
    <div className="space-y-6">
      {/* Indicator score tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
        {INDICATORS.map((i) => (
          <div key={i} className="card p-4">
            <p className="text-brand-muted text-[11px] truncate">{INDICATOR_LABELS[i]}</p>
            <p className="text-white text-2xl font-bold">
              {(stats?.indicatorAverages?.[i] ?? 0).toFixed(2)}
              <span className="text-brand-muted text-xs font-normal"> /5</span>
            </p>
          </div>
        ))}
        <div className="card p-4 border-brand-green/40">
          <p className="text-brand-muted text-[11px] flex items-center gap-1"><Star size={11} /> NPS</p>
          <p className="text-white text-2xl font-bold">{stats?.nps.score ?? 0}</p>
        </div>
        <div className="card p-4 border-emerald-500/30">
          <p className="text-brand-muted text-[11px]">Promoters</p>
          <p className="text-emerald-400 text-2xl font-bold">{stats?.nps.promoters ?? 0}</p>
        </div>
        <div className="card p-4 border-amber-500/30">
          <p className="text-brand-muted text-[11px]">Passives</p>
          <p className="text-amber-400 text-2xl font-bold">{stats?.nps.passives ?? 0}</p>
        </div>
        <div className="card p-4 border-red-500/30">
          <p className="text-brand-muted text-[11px]">Detractors</p>
          <p className="text-red-400 text-2xl font-bold">{stats?.nps.detractors ?? 0}</p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="card p-5">
          <p className="text-white font-semibold text-sm mb-1">Indicator scores</p>
          <p className="text-brand-muted text-xs mb-4">Average out of 5 across {stats?.total ?? 0} responses</p>
          <div style={{ height: 260 }}>
            {indicatorEntries.length > 0 && <HorizontalBarChart data={indicatorEntries} />}
          </div>
        </div>

        <div className="card p-5">
          <p className="text-white font-semibold text-sm mb-1">NPS breakdown</p>
          <p className="text-brand-muted text-xs mb-4">Promoters (9–10) · Passives (7–8) · Detractors (0–6)</p>
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <div className="w-full sm:w-1/2" style={{ height: 200 }}>
              {npsBreakdown.length > 0 && <Donut data={npsBreakdown} colors={CHART_COLORS} />}
            </div>
            <div className="w-full sm:w-1/2">
              <Legend data={npsBreakdown} colors={CHART_COLORS} />
            </div>
          </div>
        </div>

        <div className="card p-5">
          <p className="text-white font-semibold text-sm mb-4">Community priorities (P1)</p>
          <div style={{ height: 300 }}>
            {stats && <HorizontalBarChart data={recordToEntries(stats.priorities)} />}
          </div>
        </div>

        <div className="card p-5">
          <p className="text-white font-semibold text-sm mb-4">Program awareness (A3)</p>
          <div style={{ height: 300 }}>
            {stats && <HorizontalBarChart data={recordToEntries(stats.programAwareness)} />}
          </div>
        </div>
      </div>

      {/* Filters & toolbar */}
      <div className="card p-4 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <p className="text-white font-semibold text-sm flex items-center gap-2">
            <Filter size={14} className="text-brand-green" />
            Filters
            {activeFilterCount > 0 && (
              <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-brand-green/20 text-brand-green">
                {activeFilterCount} active
              </span>
            )}
          </p>
          <div className="flex items-center gap-2">
            {activeFilterCount > 0 && (
              <button onClick={clearFilters} className="btn-ghost text-xs">Clear all</button>
            )}
            <button onClick={handleRefresh} disabled={isFetching} className="btn-ghost text-sm">
              {isFetching ? "Refreshing…" : "Refresh"}
            </button>
            <button onClick={() => exportCsv(allRows)} disabled={allRows.length === 0} className="btn-primary text-sm flex items-center gap-1.5 disabled:opacity-50">
              <Download size={14} /> Export CSV
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-2.5">
          <div className="relative sm:col-span-2">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
            <input
              type="text"
              value={filters.search ?? ""}
              onChange={(e) => setFilter("search", e.target.value)}
              placeholder="Search by name or city…"
              className="w-full pl-9 pr-8 py-2 text-sm bg-brand-navy border border-brand-border rounded-lg text-white placeholder-brand-muted focus:outline-none focus:border-brand-green transition-colors"
            />
            {filters.search && (
              <button onClick={() => setFilter("search", undefined)} className="absolute right-2 top-1/2 -translate-y-1/2 text-brand-muted hover:text-white">
                <X size={14} />
              </button>
            )}
          </div>

          <FilterSelect value={filters.governorate}        onChange={(v) => setFilter("governorate", v)}        placeholder="All governorates"        options={govOptions} />
          <FilterSelect value={filters.role}               onChange={(v) => setFilter("role", v)}               placeholder="All roles"               options={roleOptions} />
          <FilterSelect value={filters.gender}             onChange={(v) => setFilter("gender", v)}             placeholder="All genders"             options={genderOptions} />
          <FilterSelect value={filters.ageGroup}           onChange={(v) => setFilter("ageGroup", v)}           placeholder="All age groups"          options={ageOptions} />
          <FilterSelect value={filters.professionalStatus} onChange={(v) => setFilter("professionalStatus", v)} placeholder="All statuses"            options={statusOptions} />
          <FilterSelect
            value={filters.npsBucket}
            onChange={(v) => setFilter("npsBucket", (v as NpsBucket) || undefined)}
            placeholder="All NPS buckets"
            options={["promoter", "passive", "detractor"]}
          />

          <div className="flex items-center gap-1.5">
            <input
              type="date"
              value={filters.dateFrom ?? ""}
              onChange={(e) => setFilter("dateFrom", e.target.value || undefined)}
              max={filters.dateTo}
              className="w-full py-2 px-2 text-sm bg-brand-navy border border-brand-border rounded-lg text-white focus:outline-none focus:border-brand-green"
              title="From date"
            />
            <span className="text-brand-muted text-xs">→</span>
            <input
              type="date"
              value={filters.dateTo ?? ""}
              onChange={(e) => setFilter("dateTo", e.target.value || undefined)}
              min={filters.dateFrom}
              className="w-full py-2 px-2 text-sm bg-brand-navy border border-brand-border rounded-lg text-white focus:outline-none focus:border-brand-green"
              title="To date"
            />
          </div>
        </div>

        {activeFilterCount > 0 && (
          <p className="text-brand-muted text-xs pt-1">
            Showing <span className="text-white font-semibold">{stats?.total ?? 0}</span> of{" "}
            <span className="text-white font-semibold">{metaStats?.total ?? 0}</span> responses.
          </p>
        )}
      </div>

      {/* Table */}
      {isLoading ? (
        <TableSkeleton />
      ) : isError ? (
        <div className="card p-8 text-center text-red-400">Failed to load responses. Check your API key or network.</div>
      ) : (
        <>
          <EcosystemPerceptionTable rows={pageRows} onDelete={(id) => deleteRow(id)} isDeleting={isDeleting} />
          <Pagination
            page={tableMeta.page}
            totalPages={tableMeta.totalPages}
            total={tableMeta.total}
            limit={LIMIT}
            onPageChange={setVisualPage}
            isFetching={isFetching}
          />
        </>
      )}
    </div>
  );
}

// ── Small select used in the filter row ──────────────────────────────────────

interface FilterSelectProps {
  value: string | undefined;
  onChange: (v: string | undefined) => void;
  placeholder: string;
  options: string[];
}

function FilterSelect({ value, onChange, placeholder, options }: FilterSelectProps) {
  return (
    <select
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value || undefined)}
      className="py-2 px-3 text-sm bg-brand-navy border border-brand-border rounded-lg text-white focus:outline-none focus:border-brand-green transition-colors truncate"
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
  );
}
